import Api from "../../../../../../../../../core/services/api/api";

/** When an industry has at most this many tags, load the full catalog once per import run. */
export const TAG_IMPORT_PRELOAD_MAX_COUNT = 1000;

/** Page size when paginating `searchTags` for preload. */
const TAG_SEARCH_PAGE_SIZE = 250;

/** Max parallel autocomplete calls when preload is skipped (large catalogs). */
const TAG_FALLBACK_CONCURRENCY = 8;

const normalizeTagNameKey = (name) => name?.trim()?.toUpperCase() ?? '';

/**
 * @typedef {'pending' | 'loaded' | 'skipped'} IndustryTagCatalogStatus
 */

/**
 * In-memory tag resolution for one import run.
 * - Small industry catalogs: one paginated `searchTags` preload, then O(1) lookups.
 * - Large catalogs: bounded concurrent `getTagsAutocomplete` per unique CSV name.
 */
export class TagNameLookupStore {
    constructor() {
        /** @type {Map<string, { id: *, info: * } | null>} */
        this._entryByKey = new Map();
        /**
         * @type {Map<string, {
         *   status: IndustryTagCatalogStatus,
         *   tagsByNameKey: Map<string, *>,
         *   totalCount: number,
         * }>}
         */
        this._industryCatalogById = new Map();
    }

    clear() {
        this._entryByKey.clear();
        this._industryCatalogById.clear();
    }

    #cacheKey(industryId, nameTrimmed) {
        return `${industryId ?? ''}:${normalizeTagNameKey(nameTrimmed)}`;
    }

    /**
     * Adds a tag created during preventative correction into the run cache.
     *
     * @param {string|number} industryId
     * @param {{ id: *, name?: string }} tag
     */
    registerCreatedTag(industryId, tag) {
        if (industryId == null || industryId === '' || tag?.id == null) {
            return;
        }
        const industryKey = String(industryId);
        const nameKey = normalizeTagNameKey(tag?.name);
        if (!nameKey) {
            return;
        }

        let catalog = this._industryCatalogById.get(industryKey);
        if (!catalog) {
            catalog = {status: 'loaded', tagsByNameKey: new Map(), totalCount: 0};
            this._industryCatalogById.set(industryKey, catalog);
        } else if (catalog.status === 'skipped') {
            catalog.status = 'loaded';
        }
        catalog.tagsByNameKey.set(nameKey, tag);
        catalog.totalCount = Math.max(catalog.totalCount, catalog.tagsByNameKey.size);

        const cacheKey = this.#cacheKey(industryId, tag.name);
        this._entryByKey.set(cacheKey, {id: tag.id, info: tag});
    }

    /**
     * @param {string|number} industryId
     */
    async #ensureIndustryCatalog(industryId) {
        const industryKey = String(industryId);
        const existing = this._industryCatalogById.get(industryKey);
        if (existing && existing.status !== 'pending') {
            return existing;
        }

        const catalog = existing ?? {
            status: 'pending',
            tagsByNameKey: new Map(),
            totalCount: 0,
        };
        this._industryCatalogById.set(industryKey, catalog);

        const probeResponse = await Api.searchTags({
            industryId,
            paginationDs: {pageSize: 1, currentPage: 1},
        });
        const totalCount = probeResponse?.data?.totalCount ?? 0;
        catalog.totalCount = totalCount;

        if (totalCount > 0 && totalCount <= TAG_IMPORT_PRELOAD_MAX_COUNT) {
            let fetched = 0;
            let currentPage = 1;

            while (fetched < totalCount) {
                const pageResponse = await Api.searchTags({
                    industryId,
                    paginationDs: {pageSize: TAG_SEARCH_PAGE_SIZE, currentPage},
                });
                const items = pageResponse?.data?.items ?? [];
                if (!items.length) {
                    break;
                }
                for (const tag of items) {
                    const nameKey = normalizeTagNameKey(tag?.name);
                    if (nameKey) {
                        catalog.tagsByNameKey.set(nameKey, tag);
                    }
                }
                fetched += items.length;
                currentPage += 1;
            }
            catalog.status = 'loaded';
        } else {
            catalog.status = 'skipped';
        }

        return catalog;
    }

    /**
     * @param {string} nameTrimmed
     * @param {string|number} industryId
     */
    async #fetchTagFromAutocomplete(nameTrimmed, industryId) {
        const response = await Api.getTagsAutocomplete(nameTrimmed, industryId, undefined);
        const items = response?.data ?? [];
        const key = normalizeTagNameKey(nameTrimmed);
        return items.find((t) => normalizeTagNameKey(t?.name) === key) ?? null;
    }

    /**
     * @param {string[]} names
     * @param {string|number} industryId
     */
    async #resolveMissingByAutocomplete(names, industryId) {
        if (!names.length) {
            return;
        }

        let nextIndex = 0;
        const worker = async () => {
            while (nextIndex < names.length) {
                const index = nextIndex;
                nextIndex += 1;
                const name = names[index];
                const cacheKey = this.#cacheKey(industryId, name);
                const data = await this.#fetchTagFromAutocomplete(name, industryId);
                if (data?.id != null && data.id !== '') {
                    this._entryByKey.set(cacheKey, {id: data.id, info: data});
                } else {
                    this._entryByKey.set(cacheKey, null);
                }
            }
        };

        const concurrency = Math.min(TAG_FALLBACK_CONCURRENCY, names.length);
        await Promise.all(Array.from({length: concurrency}, () => worker()));
    }

    /**
     * @param {string[]} names
     * @param {string|number} industryId
     * @param {Map<string, *>} tagsByNameKey
     */
    #resolveFromPreloadedCatalog(names, industryId, tagsByNameKey) {
        for (const name of names) {
            const cacheKey = this.#cacheKey(industryId, name);
            const nameKey = normalizeTagNameKey(name);
            const tag = tagsByNameKey.get(nameKey);
            if (tag?.id != null && tag.id !== '') {
                this._entryByKey.set(cacheKey, {id: tag.id, info: tag});
            } else {
                this._entryByKey.set(cacheKey, null);
            }
        }
    }

    /**
     * @param {string[]} tagNamesTrimmed
     * @param {string|number} industryId
     * @param {boolean} [disableCache=false]
     * @returns {Promise<{tagIds: Record<string, *>, tagInfo: Record<string, *>}>}
     */
    async resolve(tagNamesTrimmed, industryId, disableCache = false) {
        const unique = [...new Set((tagNamesTrimmed ?? []).map((n) => n?.trim()).filter(Boolean))];
        if (!unique.length || industryId == null || industryId === '') {
            return {tagIds: {}, tagInfo: {}};
        }

        const catalog = await this.#ensureIndustryCatalog(industryId);

        const namesToResolve = unique.filter((name) => {
            const cacheKey = this.#cacheKey(industryId, name);
            return disableCache || !this._entryByKey.has(cacheKey);
        });

        if (catalog.status === 'loaded') {
            this.#resolveFromPreloadedCatalog(namesToResolve, industryId, catalog.tagsByNameKey);
        } else {
            await this.#resolveMissingByAutocomplete(namesToResolve, industryId);
        }

        const tagIds = {};
        const tagInfo = {};
        for (const name of unique) {
            const cacheKey = this.#cacheKey(industryId, name);
            const entry = this._entryByKey.get(cacheKey);
            const nameKey = normalizeTagNameKey(name);
            if (entry) {
                tagIds[nameKey] = entry.id;
                tagInfo[nameKey] = entry.info;
            }
        }
        return {tagIds, tagInfo};
    }
}
